<template>
    <WorkScheduleMarker
        class="work-schedule-position"
        :class="itemClasses"
        :style="itemStyle"
        :fulfillmentRequirement="workSchedulePosition.fulfillmentRequirement"
        :title="workSchedulePosition.name"
        draggable="true"
        @dragstart="handleDragStart"
        @dragend="handleDragEnd"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
    >
        <div class="work-schedule-item-content" :style="itemStyle">
            <WorkScheduleAssignment
                v-for="workScheduleAssignment in workSchedulePosition.workScheduleAssignments ?? []"
                :key="workScheduleAssignment.id"
                :workScheduleAssignment="workScheduleAssignment"
                :workSchedulePosition="workSchedulePosition"
                :beginDateTimeWorkSchedule="workSchedulePosition.beginDateTime"
                :endDateTimeWorkSchedule="workSchedulePosition.endDateTime"
            />
            <div class="name">{{ workSchedulePosition.name }}</div>
        </div>
    </WorkScheduleMarker>
</template>

<script lang="ts">
    import {CSSProperties} from 'vue';
    import WorkScheduleUtil from '@/util/WorkScheduleUtil';
    import {WorkScheduleGridPosition, DateRange} from '@/types/types';
    import {WorkSchedulePositionDragData} from '@/types';
    import WorkScheduleAssignment from '@/components/WorkSchedule/WorkScheduleAssignment.vue';
    import {PlanbordWorkSchedulePosition, PlanbordWorkScheduleGroupLabel} from '@/types/api-object-extensions';
    import WorkScheduleMarker from '@/components/WorkSchedule/WorkScheduleMarker.vue';
    import TooltipWorkSchedule from '@/components/WorkSchedule/Tooltip/TooltipWorkSchedule.vue';
    import {ColorUtil} from '@cquential/ui';
    import {Component, Vue, Prop} from 'vue-facing-decorator';

    @Component({
        components: {
            WorkScheduleAssignment,
            WorkScheduleMarker,
        },
        emits: [
            'dragStart:workSchedulePosition',
            'dragEnd:workSchedulePosition',
        ],
    })
    export default class WorkSchedulePosition extends Vue {
        @Prop({required: true}) public renderedDateRange!: DateRange;
        @Prop({required: true}) public workSchedulePosition!: PlanbordWorkSchedulePosition;
        @Prop({required: true}) public groupLabels!: Record<number, PlanbordWorkScheduleGroupLabel>;
        @Prop({required: true}) public row!: number;

        public get itemStyle(): CSSProperties {
            const gridPosition: WorkScheduleGridPosition = WorkScheduleUtil.getGridPositionInHours(this.workSchedulePosition.beginDateTime, this.workSchedulePosition.endDateTime, this.renderedDateRange);

            const css: CSSProperties = {
                '--offset-left': 'calc((var(--left-space-holder-hours) + ' + gridPosition.left + ') * var(--hour-cell-width))',
                '--width': 'calc(' + gridPosition.width + ' * var(--hour-cell-width))',
                '--grid-row': this.row,
            };

            const groupHexColor: string | null = this.groupLabels[this.workSchedulePosition.workScheduleGroupLabelId!].color;
            if (groupHexColor && ColorUtil.isValidHexColor(groupHexColor)) {
                css.backgroundColor = this.groupLabels[this.workSchedulePosition.workScheduleGroupLabelId!].color!;
                css.color = ColorUtil.getTextContrastColor(this.groupLabels[this.workSchedulePosition.workScheduleGroupLabelId!].color!);
            }

            return css;
        }

        public get itemClasses(): Record<string, boolean> {
            return {
                'can-be-dragged': this.canBeDragged,
            };
        }

        public get canBeDragged(): boolean {
            return this.workSchedulePosition.timeBlocksWithoutWorkScheduleAssignment.length !== 0;
        }

        public handleMouseEnter(event: MouseEvent): void {
            /** @see TooltipWorkSchedule.openTooltip() */
            // @ts-expect-error TS2339
            TooltipWorkSchedule.__vfdConstructor.openTooltip(event.target! as HTMLElement, this.workSchedulePosition);
        }

        public handleMouseLeave(event: MouseEvent): void {
            /** @see TooltipWorkSchedule.closeTooltip() */
            // @ts-expect-error TS2339
            TooltipWorkSchedule.__vfdConstructor.closeTooltip(event.relatedTarget === null ? undefined : event.relatedTarget as HTMLElement);
        }

        public handleDragStart(event: DragEvent): void {
            if (!this.canBeDragged) {
                event.preventDefault();

                return;
            }
            /** @see TooltipWorkSchedule.closeTooltip() */
            // @ts-expect-error TS2339
            TooltipWorkSchedule.__vfdConstructor.closeTooltip();
            const data: WorkSchedulePositionDragData = {
                workSchedulePositionId: this.workSchedulePosition.id!,
            };
            event.dataTransfer!.setData('text', JSON.stringify(data));

            this.$emit('dragStart:workSchedulePosition', this.workSchedulePosition);
        }

        public handleDragEnd(): void {
            this.$emit('dragEnd:workSchedulePosition', this.workSchedulePosition);
        }
    }
</script>

<style lang="scss" scoped>
    .work-schedule-position {
        grid-column: 1;
        grid-row: var(--grid-row, auto);
        padding: var(--cell-padding);
        border-top: 1px solid var(--deployment-marker-border-top-color);
        box-shadow: var(--deployment-marker-default-shadow);
        background: var(--deployment-marker-default-background);
        transition: var(--theme-generic-transition);
        margin: var(--cell-padding) 0;
        position: relative;
        left: var(--offset-left);
        width: var(--width);
        min-width: 0;

        &:hover {
            box-shadow: var(--deployment-marker-hover-shadow);
        }

        &.can-be-dragged {
            cursor: pointer;
        }

        .name {
            position: relative;
            z-index: 1;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
        }
    }
</style>
